<script setup lang="ts">
import { computed, nextTick, onMounted, Ref, ref, watch } from 'vue';
import { ChevronDown, Plus } from 'lucide-vue-next';
import { Tab, Tabs } from '@makeabledk/vue-ui/components/tabs';
import { useIsFetching, useQueryClient } from '@tanstack/vue-query';
import { format, parseISO, subMonths } from 'date-fns';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { cloneDeep } from 'lodash-es';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, StudyPeriodDashboardPayload } from '@/plugins/store/actions/queries/study-periods/dashboards';
import { useStudyPeriodCategories, useStudyPeriodGrowthReferences, useStudyPeriods, useStudyPeriodsFavorites } from '@/plugins/store/actions/queries/study-periods';
import CustomMultiSelect from '@/components/ui/CustomMultiSelect.vue';
import TableTab from './TableTab.vue';
import ChartTab from './ChartTab.vue';
import CategoryPicker from './CategoryPicker.vue';
import TeamsPicker from './TeamsPicker.vue';
import Button from '@/components/ui/button/Button.vue';
import Spinner from '@/components/ui/Spinner.vue';
import SaveAnalysisDialog from './SaveAnalysisDialog.vue';
import SavedAnalysesDialog from './SavedAnalysesDialog.vue';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import { StudyPeriodSetup } from '@/types';

const isFetching = useIsFetching({ queryKey: queryKeys.getDashboardRoot });
const isLoading = computed(() => isFetching.value > 0);

const animalGroupOptions = computed(() => [
    {
        value: '1',
        label: i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.weaners'),
    },
    {
        value: '2',
        label: i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.slaughterPigs'),
    },
    {
        value: '3',
        label: i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.frats'),
    },
]);
const selectedAnimalGroup = ref<string | null>(null);

const { data: studyPeriodCategories } = useStudyPeriodCategories();

const isStudyPeriodFavoritesQueryEnabled = computed(() => selectedAnimalGroup.value !== null);
const { data: studyPeriodFavorites, isLoading: isStudyPeriodFavoritesLoading } = useStudyPeriodsFavorites({ animalGroupId: selectedAnimalGroup as Ref<string> }, { enabled: isStudyPeriodFavoritesQueryEnabled });

const isGrowthReferencesQueryEnabled = computed(() => selectedAnimalGroup.value !== null);
const { data: growthReferences, isLoading: isGrowthReferencesLoading } = useStudyPeriodGrowthReferences({ animalGroupId: selectedAnimalGroup as Ref<string> }, { enabled: isGrowthReferencesQueryEnabled });

const selectedStudyPeriodFavoriteIdsState = ref<number[]>([]);
const selectedStudyPeriodFavoriteIds = computed({
    get() {
        return selectedStudyPeriodFavoriteIdsState.value.filter((f) => studyPeriodFavorites.value?.some((f2) => f2.id === f));
    },
    set(value) {
        selectedStudyPeriodFavoriteIdsState.value = value;
    },
});

const selectedGrowthReferenceIdState = ref<number | null>(null);
const selectedGrowthReferenceId = computed({
    get() {
        return selectedGrowthReferenceIdState.value;
    },
    set(value) {
        selectedGrowthReferenceIdState.value = value;
    },
});

const selectedComparisonType = ref<'within' | 'between'>('between');

const selectedCategoryOptionsState = ref<number[]>([]);
const selectedCategoryOptions = computed({
    get() {
        return selectedCategoryOptionsState.value.filter((c) => studyPeriodCategories.value?.some((c2) => c2.options.some((o) => o.id === c)));
    },
    set(value) {
        selectedCategoryOptionsState.value = value;
    },
});

const selectedStudyPeriodsState = ref<number[]>([]);
const selectedStudyPeriods = computed({
    get() {
        return selectedStudyPeriodsState.value.filter((s) => studyPeriods.value?.some((s2) => s2.id === s));
    },
    set(value) {
        selectedStudyPeriodsState.value = value;
    },
});
const selectedFromDate = ref<string>(subMonths(new Date(), 3).toISOString());
const selectedToDate = ref<string>(new Date().toISOString());

function handleGrowthReferenceChange(id: number) {
    selectedGrowthReferenceId.value = id === selectedGrowthReferenceId.value ? null : id;
}

const studyPeriodsFetchParams = computed(() => ({
    animalGroupId: selectedAnimalGroup.value!,
    from: format(new Date(selectedFromDate.value), 'yyyy-MM-dd'),
    to: format(new Date(selectedToDate.value), 'yyyy-MM-dd'),
}));
const isStudyPeriodsQueryEnabled = computed(() => selectedAnimalGroup.value !== null);
const { data: studyPeriods, isLoading: isStudyPeriodsLoading } = useStudyPeriods(studyPeriodsFetchParams, { enabled: isStudyPeriodsQueryEnabled });

const animalGroupName = computed(() => animalGroupOptions.value.find((o) => o.value === selectedAnimalGroup.value)?.label || '');
const categoryNames = computed(
    () => studyPeriodCategories.value?.filter((c) => selectedCategoryOptions.value.includes(c.id)).flatMap((c) => c.options.filter((o) => selectedCategoryOptions.value.includes(o.id)).map((o) => o.name)) || []
);
const studyPeriodNames = computed(() => studyPeriods.value?.filter((s) => selectedStudyPeriods.value.includes(s.id)).map((s) => s.name) || []);
const favoriteTeamNames = computed(() => studyPeriodFavorites.value?.filter((o) => selectedStudyPeriodFavoriteIds.value.includes(o.id)).map((o) => o.name) || []);
const growthReferenceName = computed(() => growthReferences.value?.find((o) => o.id === selectedGrowthReferenceId.value)?.name || '');
const saveAnalysisState = computed(() => ({
    comparisonType: selectedComparisonType.value,
    categoryIds: selectedCategoryOptions.value,
    animalGroupId: selectedAnimalGroup.value ? parseInt(selectedAnimalGroup.value) : undefined,
    studyPeriodIds: selectedStudyPeriods.value,
    favoriteTeamIds: selectedStudyPeriodFavoriteIds.value,
    growthReferenceId: selectedGrowthReferenceId.value ?? undefined,
}));

const selectedChartCategoriesCount = computed(() => studyPeriodCategories.value?.filter((c) => c.options.some((o) => selectedCategoryOptions.value.includes(o.id) && o.type === 'chart')).length || 0);

const teamsPickerErrorMessage = computed(() => {
    const selectedStudyPeriodsCount = selectedStudyPeriods.value.length;
    const hasChartCategorySelected = studyPeriodCategories.value?.some((c) => c.options.some((o) => selectedCategoryOptions.value.includes(o.id) && o.type === 'chart'));

    if (selectedComparisonType.value === 'between' && selectedStudyPeriodsCount > 4 && hasChartCategorySelected) {
        return i18n.global.t('views.monitoring.teamComparison.fields.teamsPicker.errorMessages.tooManyTeamsSelectedBetween');
    }

    if (selectedComparisonType.value === 'within' && selectedStudyPeriodsCount > 1) {
        return i18n.global.t('views.monitoring.teamComparison.fields.teamsPicker.errorMessages.tooManyTeamsSelectedWithin');
    }

    return null;
});
const categoryPickerErrorMessage = computed(() => {
    if (selectedComparisonType.value === 'within') {
        const chartCategoryCount = studyPeriodCategories.value?.filter((c) => c.options.some((o) => selectedCategoryOptions.value.includes(o.id) && o.type === 'chart')).length || 0;

        if (chartCategoryCount > 3) {
            return i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.errorMessages.tooManyChartCategoriesSelected');
        }
    }

    return null;
});
const favoriteTeamsErrorMessage = computed(() => {
    if (selectedChartCategoriesCount.value > 0 && selectedStudyPeriodFavoriteIds.value.length > 1) {
        return i18n.global.t('views.monitoring.teamComparison.fields.favoriteTeams.errorMessages.tooManyFavoriteTeamsSelected');
    }

    return null;
});
const hasError = computed(() => !!teamsPickerErrorMessage.value || !!categoryPickerErrorMessage.value || !!favoriteTeamsErrorMessage.value);

const isGenerateDisabled = computed(() => {
    if (selectedAnimalGroup.value === null) {
        return true;
    }

    if (selectedCategoryOptions.value.length === 0) {
        return true;
    }

    if (selectedStudyPeriods.value.length === 0) {
        return true;
    }

    return hasError.value;
});

const isGeneratingFromSetup = ref(false);

async function handleGenerateSetup(setup: StudyPeriodSetup) {
    isGeneratingFromSetup.value = true;

    selectedAnimalGroup.value = setup.animal_group_id.toString();
    selectedCategoryOptions.value = setup.categories;
    selectedStudyPeriods.value = setup.study_periods.map((s) => s.id);
    selectedStudyPeriodFavoriteIds.value = setup.favorites.map((f) => f.id);
    selectedGrowthReferenceId.value = setup.growth_reference?.id || null;

    const sortedStudyPeriods = cloneDeep(setup.study_periods).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    selectedFromDate.value = sortedStudyPeriods[0]?.start || subMonths(new Date(), 3).toISOString();
    selectedToDate.value = new Date().toISOString();

    await nextTick();

    generateDashboard();
}

const fetchState = ref<(StudyPeriodDashboardPayload & { fetchIndex: number }) | null>(null);

function generateDashboard() {
    fetchState.value = {
        animalGroupId: selectedAnimalGroup.value!,
        categoryIds: isGeneratingFromSetup.value ? selectedCategoryOptionsState.value : selectedCategoryOptions.value,
        studyPeriodIds: isGeneratingFromSetup.value ? selectedStudyPeriodsState.value : selectedStudyPeriods.value,
        favoriteTeamIds: isGeneratingFromSetup.value ? selectedStudyPeriodFavoriteIdsState.value : selectedStudyPeriodFavoriteIds.value,
        growthReferenceId: isGeneratingFromSetup.value ? selectedGrowthReferenceIdState.value! : selectedGrowthReferenceId.value!,
        fetchIndex: fetchState.value !== null ? fetchState.value.fetchIndex + 1 : 0,
    };

    if (isGeneratingFromSetup.value) {
        isGeneratingFromSetup.value = false;
    }
}

const queryClient = useQueryClient();

onMounted(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.getDashboardRoot });
});

const isSavedAnalysesDialogOpen = ref(false);
const isSaveAnalysisDialogOpen = ref(false);

const fetchStateSelectedChartCategoriesCount = computed(
    () => fetchState.value?.categoryIds.filter((c) => studyPeriodCategories.value?.some((c2) => c2.options.some((o) => o.id === c && o.type === 'chart'))).length || 0
);
const fetchStateSelectedTableCategoriesCount = computed(
    () => fetchState.value?.categoryIds.filter((c) => studyPeriodCategories.value?.some((c2) => c2.options.some((o) => o.id === c && o.type === 'table'))).length || 0
);

const hideChartTab = computed(() => fetchStateSelectedTableCategoriesCount.value > 0 && fetchStateSelectedChartCategoriesCount.value === 0);
const hideTableTab = computed(() => fetchStateSelectedChartCategoriesCount.value > 0 && fetchStateSelectedTableCategoriesCount.value === 0);

const chartTab = ref<InstanceType<typeof Tab> | null>(null);
const tableTab = ref<InstanceType<typeof Tab> | null>(null);

watch([hideChartTab, hideTableTab], ([newHideChartTab, newHideTableTab]) => {
    if (newHideChartTab && !newHideTableTab) {
        tableTab.value?.select?.();
    } else if (!newHideChartTab && newHideTableTab) {
        chartTab.value?.select?.();
    }
});

const teamsPickerDisplayValue = computed(() => {
    if (selectedStudyPeriods.value.length === 0) {
        return null;
    }

    if (selectedComparisonType.value === 'between') {
        return i18n.global.t('views.monitoring.teamComparison.fields.teams.texts.xOfXSelected').replace('%VALUE%', selectedStudyPeriods.value.length.toString()).replace('%MAX%', 4);
    }

    return i18n.global.t('views.monitoring.teamComparison.fields.teams.texts.xOfXSelected').replace('%VALUE%', selectedStudyPeriods.value.length.toString()).replace('%MAX%', '1');
});
const favoriteTeamsDisplayValue = computed(() => {
    if (selectedStudyPeriodFavoriteIds.value.length === 0) {
        return null;
    }

    if (selectedChartCategoriesCount.value > 0) {
        return i18n.global.t('views.monitoring.teamComparison.fields.favoriteTeams.texts.xOfXSelected').replace('%VALUE%', selectedStudyPeriodFavoriteIds.value.length.toString()).replace('%MAX%', 1);
    }

    return i18n.global
        .t('views.monitoring.teamComparison.fields.favoriteTeams.texts.xOfXSelected')
        .replace('%VALUE%', selectedStudyPeriodFavoriteIds.value.length.toString())
        .replace('%MAX%', studyPeriodFavorites.value?.length.toString() || '0');
});

onMounted(() => {
    chartTab.value?.select?.();
});
</script>

<template>
    <DashboardContentLayout class="space-y-8" :heading="$t('views.monitoring.teamComparison.headings.title')" :has-pig-type-picker="false">
        <div>
            <Button size="lg" class="min-w-40 px-5" variant="secondary" @click="isSavedAnalysesDialogOpen = true">
                <Plus class="w-5 h-5 -ml-1" />
                {{ $t('views.monitoring.teamComparison.buttons.savedAnalyses') }}
            </Button>
            <div class="relative mt-6">
                <Listbox v-slot="{ open }" v-model="selectedAnimalGroup">
                    <ListboxButton class="w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left">
                        <div class="flex items-center gap-x-1 leading-normal">
                            <span class="font-medium">{{ $t('views.monitoring.teamComparison.fields.animalGroup.label') }}:</span>
                            <span class="text-gray-500">
                                {{ animalGroupOptions.find((o) => o.value === selectedAnimalGroup)?.label || $t('views.monitoring.teamComparison.fields.animalGroup.texts.noSelection') }}
                            </span>
                        </div>
                        <ChevronDown class="flex-shrink-0 h-5 w-5 transition-transform" :class="{ 'rotate-180': open }" aria-hidden="true" />
                    </ListboxButton>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-100 ease-out"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <ListboxOptions class="absolute z-20 mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <ListboxOption v-for="option in animalGroupOptions" :key="option.value" v-slot="{ active, selected }" :value="option.value">
                                <li
                                    class="py-2 px-3 h-full truncate block w-full text-left cursor-pointer"
                                    :class="{
                                        'bg-primary-300 bg-opacity-40': selected,
                                        'hover:bg-gray-100': !selected,
                                        'bg-gray-100': active && !selected,
                                    }"
                                >
                                    {{ option.label }}
                                </li>
                            </ListboxOption>
                        </ListboxOptions>
                    </transition>
                </Listbox>
            </div>
            <CategoryPicker
                v-model:selected-comparison-type="selectedComparisonType"
                v-model:selected-category-options="selectedCategoryOptions"
                :categories="studyPeriodCategories || []"
                :error-message="categoryPickerErrorMessage"
            />
            <TeamsPicker
                v-model:selected-study-periods="selectedStudyPeriods"
                v-model:selected-from-date="selectedFromDate"
                v-model:selected-to-date="selectedToDate"
                :display-value="teamsPickerDisplayValue"
                :study-periods="studyPeriods"
                :disabled="selectedAnimalGroup === null"
                :loading="isStudyPeriodsLoading"
                :error-message="teamsPickerErrorMessage"
            />
            <CustomMultiSelect
                v-model:value="(selectedStudyPeriodFavoriteIds as any)"
                :items="studyPeriodFavorites || []"
                item-value="id"
                item-text="name"
                class="mt-3"
                :class="{ 'opacity-60 pointer-events-none': selectedAnimalGroup === null }"
                flat-values
                :disabled="!selectedAnimalGroup"
            >
                <template #trigger="{ displayValue, expanded }">
                    <div
                        class="mb-3 w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left transition-colors"
                        :class="{ 'border-red-500': favoriteTeamsErrorMessage }"
                    >
                        <div class="flex items-center gap-x-1 leading-normal">
                            <span class="font-medium">{{ $t('views.monitoring.teamComparison.fields.favoriteTeams.label') }}: </span>
                            <span class="text-gray-500">
                                {{ favoriteTeamsDisplayValue || displayValue }}
                            </span>
                        </div>
                        <Spinner v-if="isStudyPeriodFavoritesLoading" class="flex-shrink-0 h-5 w-5" />
                        <ChevronDown v-else class="flex-shrink-0 h-5 w-5 transition-transform" :class="{ 'rotate-180': expanded }" aria-hidden="true" />
                    </div>
                </template>
                <template #item="{ item, text }">
                    <div class="flex grow justify-between max-w-full min-w-0 items-center gap-x-2 pr-5">
                        <div class="whitespace-nowrap truncate min-w-0">
                            {{ text }} <span v-if="item.locationNameAugmented" class="text-gray-500">({{ item.locationNameAugmented }})</span>
                        </div>
                        <div v-if="item.start && item.end" class="text-gray-500 text-sm whitespace-nowrap">
                            {{ $d(parseISO(item.start as string), 'short') }}{{ item.end ? ` - ${$d(parseISO(item.end as string), 'short')}` : '' }}
                        </div>
                    </div>
                </template>
                <template #append>
                    <ExpandCollapseTransition>
                        <div v-if="favoriteTeamsErrorMessage" class="sticky bottom-0 bg-white py-3 px-4 border-t">
                            <div class="text-red-500 text-right font-medium">
                                {{ favoriteTeamsErrorMessage }}
                            </div>
                        </div>
                    </ExpandCollapseTransition>
                </template>
            </CustomMultiSelect>
            <div class="relative">
                <Listbox v-slot="{ open }" :model-value="selectedGrowthReferenceId" @update:model-value="handleGrowthReferenceChange">
                    <ListboxButton
                        class="w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left disabled:opacity-60 disabled:pointer-events-none"
                        :disabled="selectedAnimalGroup === null"
                    >
                        <div class="flex items-center gap-x-1 leading-normal">
                            <span class="font-medium">{{ $t('views.monitoring.teamComparison.fields.growthReference.label') }}:</span>
                            <span class="text-gray-500">
                                {{ growthReferences?.find((o) => o.id === selectedGrowthReferenceId)?.name || $t('views.monitoring.teamComparison.fields.growthReference.placeholder') }}
                            </span>
                        </div>
                        <Spinner v-if="isGrowthReferencesLoading" class="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                        <ChevronDown v-else class="flex-shrink-0 h-5 w-5 transition-transform" :class="{ 'rotate-180': open }" aria-hidden="true" />
                    </ListboxButton>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-100 ease-out"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <ListboxOptions class="absolute z-20 mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <ListboxOption v-for="option in growthReferences" :key="option.id" v-slot="{ active, selected }" :value="option.id">
                                <li
                                    class="py-2 px-3 h-full truncate block w-full text-left cursor-pointer"
                                    :class="{
                                        'bg-primary-300 bg-opacity-40': selected,
                                        'hover:bg-gray-100': !selected,
                                        'bg-gray-100': active && !selected,
                                    }"
                                >
                                    {{ option.name }}
                                </li>
                            </ListboxOption>
                        </ListboxOptions>
                    </transition>
                </Listbox>
            </div>
            <div class="flex items-center gap-x-4 justify-end mt-6">
                <Button size="lg" class="min-w-40 px-5 min-h-12" variant="secondary" :disabled="isGenerateDisabled || isLoading" @click="isSaveAnalysisDialogOpen = true">
                    <Plus class="w-5 h-5 -ml-1" />
                    {{ $t('views.monitoring.teamComparison.buttons.saveAnalysis') }}
                </Button>
                <Button size="lg" class="min-w-40 px-5 min-h-12" :disabled="isGenerateDisabled || isLoading" @click="generateDashboard">
                    {{ $t('views.monitoring.teamComparison.buttons.generate') }}
                </Button>
            </div>
        </div>
        <div class="default-tabs">
            <Tabs teleport="#team-comparison-tabs-target" active-tab-class="--active">
                <nav class="space-x-8 flex border-b border-black border-opacity-10">
                    <div v-if="!hideChartTab" class="flex">
                        <Tab ref="chartTab" :name="$t('views.monitoring.teamComparison.tabs.chart')">
                            <ChartTab :fetch-state="fetchState" />
                        </Tab>
                    </div>
                    <div v-if="!hideTableTab" class="flex">
                        <Tab ref="tableTab" :name="$t('views.monitoring.teamComparison.tabs.table')">
                            <TableTab :fetch-state="fetchState" />
                        </Tab>
                    </div>
                </nav>
            </Tabs>
            <div id="team-comparison-tabs-target" class="mt-6" />
        </div>
        <SaveAnalysisDialog
            v-model:open="isSaveAnalysisDialogOpen"
            :state="saveAnalysisState"
            :comparison-type="selectedComparisonType"
            :category-names="categoryNames"
            :animal-group-name="animalGroupName"
            :study-period-names="studyPeriodNames"
            :favorite-team-names="favoriteTeamNames"
            :growth-reference-name="growthReferenceName"
        />
        <SavedAnalysesDialog v-model:open="isSavedAnalysesDialogOpen" :categories="studyPeriodCategories || []" @generate="handleGenerateSetup" />
    </DashboardContentLayout>
</template>
